import { index, modelOptions, mongoose, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { ThirdPartyApiValidationResult } from "../../Configurations/ThirdPartyApiValidation.js";
import { ObjectIdString } from "../../validators/index.js";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    ThirdPartyApiCredentialActivityFieldsWithDefaults,
    ThirdPartyApiCredentialActivitySchemaTypes,
} from "../types/thirdPartyApiCredentialActivity.d.js";
import type { DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "ThirdPartyApiCredentialActivity" as const;

/**
 * When each background job last touched one *version* of a credential, and how that version's last
 * validation went. Upserted in place by the jobs.
 *
 * One document per credential version, not per record. A new version has no activity document, so
 * every edit forces re-validation rather than inheriting the previous version's verdict — which is
 * what keeps a rotated key from reading as valid on the strength of the old one.
 *
 * Not versioned itself, and deliberately off {@link ThirdPartyApiCredential}: these fields move on
 * every job run, which on the SCD document would mint a version per run and bury the real edits.
 */
@index({ credentialRecordId: 1, credentialVersionId: 1 }, { unique: true })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: SCHEMA_KEY,
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<ThirdPartyApiCredentialActivity>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ThirdPartyApiCredentialActivity extends SchemaBase<
    ThirdPartyApiCredentialActivity,
    ThirdPartyApiCredentialActivityFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /**
     * The credential's `recordId`, grouping every version's activity for one logical credential.
     * Not a `ref`: it matches many credential documents, one per version.
     */
    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true })
    public credentialRecordId!: mongoose.Types.ObjectId;

    /** Which version of that credential this activity describes. */
    @validate({ zodValidator: z.number().int().positive(), responseOnly: true })
    @prop({ type: () => Number, required: true })
    public credentialVersionId!: number;

    /** When we last checked this version, whatever the outcome. */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastValidatedAt?: Date;

    @validate({
        zodValidator: z.enum(Object.values(ThirdPartyApiValidationResult) as [string, ...string[]]),
        responseOnly: true,
    })
    @prop({
        type: () => String,
        enum: ThirdPartyApiValidationResult,
        required: true,
        default: ThirdPartyApiValidationResult.NOT_VALIDATED,
    })
    public lastValidationResult: ThirdPartyApiValidationResult = ThirdPartyApiValidationResult.NOT_VALIDATED;

    /** When data was last pulled using this version. Earlier versions' syncs live on their own documents. */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastSyncedAt?: Date;

    /** When the device list was last imported using this version. */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastDeviceImportAt?: Date;

    static getters = {} as const satisfies Record<string, (self: ThirdPartyApiCredentialActivity) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<ThirdPartyApiCredentialActivitySchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<
            ThirdPartyApiCredentialActivity,
            ThirdPartyApiCredentialActivityFieldsWithDefaults
        >,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

ThirdPartyApiCredentialActivity.dtoValidators = getZodSchemaFromClass(
    ThirdPartyApiCredentialActivity,
) as unknown as DtoValidatorTypes<ThirdPartyApiCredentialActivitySchemaTypes>;
